(function () {
  'use strict';

  const STORAGE_KEY = 'askfelix.webVitalsQueue';
  const METRICS_ENDPOINT = '/api/metrics/web-vitals';
  const MAX_QUEUE_SIZE = 20;
  const REPORT_DELAY_MS = 3000;

  const state = {
    lcp: null,
    fid: null,
    cls: null,
    navigationTiming: null,
    queueFlushTimer: null
  };

  function safeReadQueue() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.warn('performance-monitor could not parse queue', err);
      return [];
    }
  }

  function persistQueue(queue) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
    } catch (err) {
      console.warn('performance-monitor could not persist queue', err);
    }
  }

  function enqueue(payload) {
    if (window.offlineManager && typeof window.offlineManager.enqueue === 'function') {
      window.offlineManager.enqueue(METRICS_ENDPOINT, payload);
      return;
    }
    const queue = safeReadQueue();
    queue.push(payload);
    persistQueue(queue);
  }

  function buildPayload() {
    return {
      timestamp: Date.now(),
      url: window.location.href,
      lcp: state.lcp,
      fid: state.fid,
      cls: state.cls,
      navigationTiming: state.navigationTiming,
      userAgent: navigator.userAgent
    };
  }

  function sendBeacon(payload) {
    const body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        const success = navigator.sendBeacon(
          METRICS_ENDPOINT,
          new Blob([body], { type: 'application/json' })
        );
        if (!success) throw new Error('sendBeacon returned false');
        return true;
      }
    } catch (err) {
      console.warn('performance-monitor sendBeacon failed', err);
    }
    return false;
  }

  function flushQueue() {
    if (window.offlineManager && typeof window.offlineManager.flush === 'function') {
      window.offlineManager.flush();
      return;
    }

    const queue = safeReadQueue();
    if (!queue.length) return;

    const remaining = [];
    queue.forEach(entry => {
      if (!entry || typeof entry !== 'object') return;
      if (!sendBeacon(entry)) {
        remaining.push(entry);
      }
    });
    persistQueue(remaining);
  }

  function scheduleFlush() {
    if (state.queueFlushTimer) return;
    state.queueFlushTimer = window.setTimeout(() => {
      state.queueFlushTimer = null;
      flushQueue();
    }, REPORT_DELAY_MS);
  }

  function recordMetric(metric, value) {
    if (value == null) return;
    state[metric] = value;
    scheduleFlush();
  }

  function observeWebVitals() {
    if (!('PerformanceObserver' in window)) {
      console.warn('performance-monitor: PerformanceObserver unavailable');
      return;
    }

    try {
      const lcpObserver = new PerformanceObserver(list => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          recordMetric('lcp', {
            value: lastEntry.startTime,
            element: lastEntry.element ? lastEntry.element.tagName : null
          });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (err) {
      console.warn('performance-monitor LCP observer failed', err);
    }

    try {
      const fidObserver = new PerformanceObserver(list => {
        const firstEntry = list.getEntries()[0];
        if (firstEntry) {
          recordMetric('fid', {
            value: firstEntry.processingStart - firstEntry.startTime,
            name: firstEntry.name
          });
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (err) {
      console.warn('performance-monitor FID observer failed', err);
    }

    try {
      const clsObserver = new PerformanceObserver(list => {
        list.getEntries().forEach(entry => {
          if (!entry.hadRecentInput) {
            const current = state.cls && state.cls.value ? state.cls.value : 0;
            recordMetric('cls', {
              value: current + entry.value
            });
          }
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (err) {
      console.warn('performance-monitor CLS observer failed', err);
    }
  }

  function captureNavigationTiming() {
    if (!('performance' in window) || !performance.timing) return;
    const timing = performance.timing;
    state.navigationTiming = {
      domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
      loadEvent: timing.loadEventEnd - timing.navigationStart,
      responseEnd: timing.responseEnd - timing.navigationStart,
      requestStart: timing.requestStart - timing.navigationStart
    };
  }

  function immediateReport() {
    const payload = buildPayload();
    if (!sendBeacon(payload)) {
      enqueue(payload);
    }
  }

  function flushListener(isOnline) {
    if (isOnline) {
      flushQueue();
    }
  }

  function init() {
    captureNavigationTiming();
    observeWebVitals();

    if (window.offlineManager) {
      window.offlineManager.onStatusChange(flushListener);
    } else {
      window.addEventListener('online', () => flushQueue());
    }

    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        immediateReport();
        flushQueue();
      }
    });

    window.addEventListener('pagehide', () => {
      immediateReport();
      flushQueue();
    });

    // Fallback report after initial delay
    window.setTimeout(immediateReport, REPORT_DELAY_MS);
  }

  const api = {
    record(metricName, value) {
      recordMetric(metricName, value);
    },
    flush: flushQueue,
    snapshot() {
      return buildPayload();
    }
  };

  if (!window.performanceMonitor) {
    window.performanceMonitor = api;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
